import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { isApiError } from "@bw/shared/api";
import { playerHandleSuffix } from "@bw/shared/components";
import type { BookingParticipantResponse } from "@bw/shared/types/generated";
import { useRemoveBookingParticipant } from "../hooks/useRemoveBookingParticipant";

/**
 * The host's per-guest Remove control: an inline Remove button that opens a
 * confirm dialog before the destructive call. On confirm it calls
 * useRemoveBookingParticipant(facilityId, bookingId, guestUid); the roster
 * REFETCHES on success (never an optimistic local removal). A failure (e.g. 409
 * the guest already checked in) surfaces a role="alert" and KEEPS the dialog open.
 *
 * Mounted by the page ONLY for a removable guest (never the host's own row, never
 * a checked-in guest), so the control is never a dead affordance the backend will
 * reject. MUI owns the confirm dialog's modal focus management.
 */
export function RemoveParticipantControl({
  facilityId,
  bookingId,
  participant,
}: {
  facilityId: string;
  bookingId: string;
  participant: BookingParticipantResponse;
}) {
  const [confirming, setConfirming] = useState(false);
  const remove = useRemoveBookingParticipant(facilityId, bookingId);

  const triggerRef = useRef<HTMLButtonElement>(null);
  // The resolved display_name (or the neutral "this player" when null — never the
  // raw uid). The `#abcd` handle (last-4 of user_id) is appended ONLY alongside a
  // REAL name, so the confirm reads "Remove Ana #ab12?"; an unresolved guest reads
  // "Remove this player?" with NO handle. `nameWithHandle` is the plain-string form
  // for the trigger's aria-label; the dialog <h2> renders the disc as a styled span.
  const resolvedName = participant.display_name?.trim();
  const name = resolvedName || "this player";
  const handle = resolvedName ? playerHandleSuffix(participant.user_id) : null;
  const nameWithHandle = handle ? `${name} ${handle}` : name;

  function open() {
    remove.reset();
    setConfirming(true);
  }

  function close() {
    if (remove.isPending) return;
    setConfirming(false);
    triggerRef.current?.focus();
  }

  function handleConfirm() {
    remove.mutate(participant.user_id, {
      onSuccess: () => {
        setConfirming(false);
        // Guard: if the row unmounts on success (guest removed from roster),
        // triggerRef is null and the optional-chain no-ops safely.
        triggerRef.current?.focus();
      },
      // On error the dialog stays open and surfaces remove.error.
    });
  }

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="text"
        size="small"
        onClick={open}
        aria-label={`Remove ${nameWithHandle}`}
      >
        Remove
      </Button>

      <Dialog
        open={confirming}
        onClose={() => close()}
        aria-labelledby="remove-participant-title"
        aria-describedby="remove-participant-description"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle id="remove-participant-title">
          Remove {name}
          {handle ? (
            <Box
              component="span"
              aria-hidden="true"
              sx={{ ml: 0.5, color: "text.secondary", fontWeight: 500 }}
            >
              {handle}
            </Box>
          ) : null}
          ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-participant-description">
            They&apos;ll be taken off this court&apos;s roster.
          </DialogContentText>
          {remove.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              {removeErrorMessage(remove.error)}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            type="button"
            variant="text"
            size="small"
            onClick={close}
            disabled={remove.isPending}
          >
            Keep on roster
          </Button>
          <Button
            type="button"
            color="error"
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={remove.isPending}
          >
            {remove.isPending ? "Removing…" : "Yes, remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Map a remove rejection to an honest message. */
function removeErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.status === 409) {
      return "We couldn't remove them — they've already checked in.";
    }
    return error.message || "We couldn't remove that player. Please try again.";
  }
  return "We couldn't remove that player. Please try again.";
}
